var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useEffect, useState } from "react";
import * as pc from "playcanvas";
import { clamp } from "utility/number";
import { useAppSelector, selectors } from "store";
import { connectNodes, connectNodesWithLockedEdgeLength, } from "3d/tube-rendering";
import { tubeCylinderAsset, tubeMaterials, tubeMaterialsLengthLocked, } from "3d/constants/common";
import { useInteractiveAppearance } from "hooks/playcanvas/interactive-appearance";
var MIN_DIAMETER_VISUALIZED = 0.008;
var MAX_DIAMETER_VISUALIZED = 0.012;
export var Edge3D = function (_a) {
    var app = _a.app, modelAssets = _a.modelAssets, materialAssets = _a.materialAssets, edge = _a.edge, hideEdge = _a.hideEdge, isDoubleTube = _a.isDoubleTube, skip = _a.skip, hoverId = _a.hoverId, onMiddlePositionChange = _a.onMiddlePositionChange, onCurveLengthChange = _a.onCurveLengthChange;
    var _b = __read(useState(null), 2), edgeEntity = _b[0], setEdgeEntity = _b[1];
    var objectShapeId = useAppSelector(selectors.selectObjectShape);
    var _c = useAppSelector(selectors.selectEdgeConnections(edge.id)), fromAttachment = _c.fromAttachment, toAttachment = _c.toAttachment, fromNode = _c.fromNode, toNode = _c.toNode;
    useEffect(function () {
        var entity = new pc.Entity(edge.id.toString());
        app.root.addChild(entity);
        setEdgeEntity(entity);
        return function () {
            entity.destroy();
            setEdgeEntity(null);
        };
    }, [app.root, edge.id]);
    useEffect(function () {
        if (!edgeEntity || hideEdge)
            return;
        /* Skip drawing if an endpoint can't be resolved — the edge references an
         * attachment/node that doesn't currently exist. */
        if (!fromAttachment || !toAttachment || !fromNode || !toNode)
            return;
        var tube1Entity = new pc.Entity();
        var tube2Entity = new pc.Entity();
        edgeEntity.addChild(tube1Entity);
        edgeEntity.addChild(tube2Entity);
        var isEdgeLengthLocked = edge.lengthLocked !== undefined;
        var drawTube = function (diameter, materialIndex, entity, offset) {
            var defaultConnectNodesProps = {
                app: app,
                fromNode: fromNode,
                fromAttachment: fromAttachment,
                toNode: toNode,
                toAttachment: toAttachment,
                cylinderAsset: modelAssets[tubeCylinderAsset],
                material: materialAssets[tubeMaterials[edge.tubeType][materialIndex]].resource,
                rootEntity: entity,
                diameter: diameter,
                offsetX: offset !== null && offset !== void 0 ? offset : 0,
                skip: skip,
            };
            if (!isEdgeLengthLocked) {
                return connectNodes(__assign({}, defaultConnectNodesProps));
            }
            return connectNodesWithLockedEdgeLength(__assign(__assign({}, defaultConnectNodesProps), { lengthLockedMaterial: materialAssets[tubeMaterialsLengthLocked[edge.tubeType][materialIndex]].resource }));
        };
        var tubeLength = 0;
        if (isDoubleTube) {
            var diameterTotal = edge.diameterDefaultTube + edge.diameterBlowOffTube;
            var diameterFractions = [
                edge.diameterDefaultTube / diameterTotal,
                edge.diameterBlowOffTube / diameterTotal,
            ];
            /* Show the two tubes as fractions of the total, where the total is clamped to the
             * min and max visualized values */
            var diameter1Vizualized = diameterFractions[0] *
                clamp(diameterTotal, MIN_DIAMETER_VISUALIZED, MAX_DIAMETER_VISUALIZED);
            var diameter2Vizualized = diameterFractions[1] *
                clamp(diameterTotal, MIN_DIAMETER_VISUALIZED, MAX_DIAMETER_VISUALIZED);
            /* Tube should have an offset such that the tubes are centered
             * and positioned next to eachother */
            var diff = (diameter2Vizualized - diameter1Vizualized) / 2;
            var tube1Offset = diameter1Vizualized / 2 + diff;
            var tube2Offset = -diameter2Vizualized / 2 + diff;
            tubeLength = drawTube(diameter1Vizualized, 0, tube1Entity, tube1Offset);
            drawTube(diameter2Vizualized, 1, tube2Entity, tube2Offset);
        }
        else {
            tubeLength = drawTube(clamp(edge.diameterDefaultTube, MIN_DIAMETER_VISUALIZED, MAX_DIAMETER_VISUALIZED), 0, tube1Entity);
        }
        var middleSegment = tube1Entity === null || tube1Entity === void 0 ? void 0 : tube1Entity.children[Math.round((tube1Entity === null || tube1Entity === void 0 ? void 0 : tube1Entity.children.length) / 2)];
        if (middleSegment) {
            onMiddlePositionChange(middleSegment.getPosition().clone());
        }
        onCurveLengthChange(tubeLength);
        return function () {
            tube1Entity.destroy();
            tube2Entity.destroy();
        };
    }, [
        app,
        hideEdge,
        edgeEntity,
        edge.diameterDefaultTube,
        edge.diameterBlowOffTube,
        materialAssets,
        modelAssets,
        edge.tubeType,
        onCurveLengthChange,
        fromNode,
        fromAttachment,
        toNode,
        toAttachment,
        isDoubleTube,
        objectShapeId,
        edge.lengthLocked,
        edge.fromAttachmentId,
        edge.toAttachmentId,
        skip,
        onMiddlePositionChange,
    ]);
    useInteractiveAppearance(edge.id, edgeEntity, materialAssets, hoverId);
    return null;
};
